import { FC, useContext, useEffect, useState } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled from 'styled-components';

import ArrowIcon from '@/components/ArrowIcon/ArrowIcon';
import LocalizedLink from '@/components/LocalizedLink/LocalizedLink';
import HubspotForm from '@/components/Modal/HubspotForm';
import HubspotFormModal from '@/components/Modal/HubspotFormModal';
import {
	CURRENT_YEAR,
	HUBSPOT_FORM_IDS,
	HUBSPOT_FORM_TITLES,
	STRAPI_LANGUAGES,
} from '@/lib/constants';
import { getImage } from '@/lib/fetchAPI';
import { GlobalContext } from '@/pages/_app';

interface Props {
	lang: string;
	homepage?: boolean;
}

const Footer: FC<Props> = ({ homepage = false, lang }: Props) => {
	const { navigation, footer } = useContext(GlobalContext);
	const [data, setData] = useState<any>(null);
	const [currentFormId, setCurrentFormId] = useState<string | null>(null);

	const openModal = (formId: string) => setCurrentFormId(formId);

	const closeModal = () => setCurrentFormId(null);

	useEffect(() => {
		if (!footer || !navigation) return;

		const strapiLocale = STRAPI_LANGUAGES[lang];

		const footerLocaleVersion =
			footer.locale === strapiLocale
				? footer
				: footer.localizations?.find((item) => item.locale === strapiLocale);
		const navigationLocaleVersion =
			navigation.locale === strapiLocale
				? navigation
				: navigation.localizations?.find(
						(item) => item.locale === strapiLocale,
					);
		const localeVersion = {
			footer: footerLocaleVersion,
			navigation: navigationLocaleVersion,
		};

		setData(localeVersion);
	}, [lang, footer, navigation]);

	return (
		<FooterWrapper>
			<Left>
				<LogoRow>
					<ScrollAnimation
						animateOnce
						offset={50}
						duration={0.6}
						animateIn="fadeIn"
					>
						<LocalizedLink href="/">
							<span>
								<ImageNext
									alt="Viber Footer Logo"
									src={getImage(data?.footer?.logoViber)}
								/>
							</span>
						</LocalizedLink>
					</ScrollAnimation>
				</LogoRow>

				<Copyright>© {CURRENT_YEAR} Viber Media S.à r.l.</Copyright>
			</Left>

			<Center>
				{data?.footer?.footerItem?.map((item, index: number) => (
					<MenuItem key={`footer_item_${index}`}>
						<ScrollAnimation
							animateOnce
							offset={30}
							duration={0.5}
							animateIn="fadeIn"
							delay={(index + 1) * 50}
						>
							{item?.name?.trim() === 'Contact Us' ? (
								<button
									type="button"
									onClick={() => openModal(HUBSPOT_FORM_IDS.MAIN)}
								>
									{item.name}
								</button>
							) : item?.name?.trim() === 'Newsletter' ? (
								<button
									type="button"
									onClick={() => openModal(HUBSPOT_FORM_IDS.MAIN)}
								>
									{item.name}
								</button>
							) : (
								<a href={item.link} rel={item.rel} target={item.target}>
									{item.name}
								</a>
							)}
						</ScrollAnimation>
					</MenuItem>
				))}
			</Center>

			<SocialsWrapper>
				<Wrapper>
					<SelectedItem>
						<div>{data?.navigation?.socialMedia?.title}</div>

						<ArrowIcon />
					</SelectedItem>

					<DropdownWrapper>
						<Triangle
							alt="dropdown-triangle"
							src="/images/icons/dropdown-triangle.png"
						/>

						<Shades />

						<DropdownList>
							<DesktopSocials>
								{data?.navigation?.socialMedia?.items?.map((item, index) => (
									<DesktopBlock
										key={`footer_social_desktop_${index}`}
										href={item.link}
										target={item.target}
									>
										<Icon alt={item.name} src={getImage(item.logo)} />
									</DesktopBlock>
								))}
							</DesktopSocials>
						</DropdownList>
					</DropdownWrapper>
				</Wrapper>
			</SocialsWrapper>

			<Right>
				<ScrollAnimation
					animateOnce
					animateIn="fadeIn"
					duration={0.6}
					offset={50}
				>
					{homepage ? (
						<LocalizedLink href={data?.footer?.qreamLink}>
							<span>
								<ImageNext
									alt="Qream Logo"
									src={getImage(data?.footer?.logoQream)}
								/>
							</span>
						</LocalizedLink>
					) : null}
				</ScrollAnimation>
			</Right>

			<MobileSocials>
				<Socials>
					{data?.navigation?.socialMedia?.items?.map((item, index) => (
						<Block
							key={`footer_social_mobile_${index}`}
							href={item.link}
							target={item.target}
						>
							<Icon src={getImage(item.logo)} alt={item.name} />
						</Block>
					))}
				</Socials>
			</MobileSocials>

			{currentFormId ? (
				<HubspotFormModal handleClose={closeModal}>
					<h2>{HUBSPOT_FORM_TITLES.MAIN}</h2>

					<div className="subtitleModal">
						The Rakuten Viber B2B Marketing team would love to contact you about
						upcoming events, webinars, whitepapers, learning, giveaways and
						more.
					</div>

					<HubspotForm formId={currentFormId} handleClose={closeModal} />
				</HubspotFormModal>
			) : null}
		</FooterWrapper>
	);
};

const FooterWrapper = styled.div`
	position: relative;
	z-index: 500;
	padding-top: 30px;
	padding-bottom: 25px;
	background-color: var(--color-white);

	width: 1320px;
	max-width: 95%;
	margin: 0 auto;

	display: flex;
	align-items: center;
	justify-content: space-between;
	text-align: center;

	@media (min-width: 1600px) {
		width: 1420px;
	}

	@media (min-width: 1750px) {
		width: 1520px;
	}

	@media (min-width: 1900px) {
		width: 1650px;
	}

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: center;
	}

	@media (max-width: 600px) {
		padding-top: 30px;
		padding-bottom: 25px;
		flex-direction: column;
		align-items: center;
	}

	@media (max-width: 535px) {
		padding: 0 10px;
		width: 100%;
		max-width: 100%;
	}
`;

const Left = styled.div`
	display: flex;
	flex-direction: column;

	@media (max-width: 768px) {
		order: 2;
		padding-top: 20px;
	}
`;

const Copyright = styled.div`
	font-family: var(--font-sans);

	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-purple-50);
	opacity: 0.4;
	margin-top: 8px;

	@media (max-width: 1290px) {
		font-size: 13px;
	}

	@media (max-width: 1180px) {
		font-size: 11.5px;
	}

	@media (max-width: 1060px) {
		font-size: 11px;
	}
`;

const MenuItem = styled.div`
	transition: 0.5s;
	pointer-events: auto;
	display: flex;
	cursor: pointer;
	position: relative;
	display: flex;
	flex-direction: column;
	margin: 0 7px;

	@media (min-width: 1601px) {
		margin: 0 14px;
	}

	&:after {
		content: '';
		display: block;
		width: 0;
		height: 2px;
		background-color: var(--color-purple);
		transition: width 0.2s;
	}

	&:hover:after {
		width: 100%;
		transition: width 0.2s linear;
	}

	a,
	button {
		font-family: var(--font-sans);
		background-color: transparent;

		font-size: 14px;
		line-height: 20px;
		color: var(--color-purple);
		transition: 0.3s;
		text-decoration: none;

		@media (min-width: 1601px) {
			font-size: 16px;
			line-height: 24px;
		}
	}
`;

const Center = styled.div`
	display: flex;
	align-items: center;

	@media (max-width: 1340px) {
		order: 2;
		width: 55%;
		flex-wrap: wrap;
		justify-content: center;
	}

	@media (max-width: 1140px) {
		width: 60%;
	}

	@media (max-width: 1065px) {
		width: 65%;
	}

	@media (max-width: 940px) {
		width: 90%;
	}

	@media (max-width: 800px) {
		width: 100%;
	}

	@media (max-width: 768px) {
		flex-wrap: wrap;
		justify-content: center;
		width: 80%;
		margin: 10px 0;
	}

	@media (max-width: 640px) {
		width: 100%;
	}
`;

const SocialsWrapper = styled.div`
	@media (max-width: 900px) {
		display: none;
	}
`;

const Right = styled.div`
	width: max-content;

	@media (max-width: 1340px) {
		order: 3;
	}

	@media (max-width: 768px) {
		padding-left: 47px;
	}
`;

const MobileSocials = styled.div`
	display: none;

	@media (max-width: 768px) {
		display: block;
		order: 1;
	}
`;

const Socials = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	padding: 0 13px;
`;

const Block = styled.a`
	width: 60px;
	height: 60px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	background-color: var(--color-white-30);
	border-radius: 12px;
	margin: 8px;
	transition: background-color 0.3s;
	cursor: pointer;

	&:hover {
		svg path {
			fill: var(--color-white);
		}
	}

	svg {
		width: 16px;
		display: block;
		margin: auto;

		path {
			transition: fill 0.3s;
		}
	}

	@media (max-width: 350px) {
		margin: 5px;
	}
`;

const LogoRow = styled.div`
	a {
		display: flex;
		align-items: center;
		font-family: var(--font-sans);
		font-size: 14px;
		line-height: 20px;
		color: var(--color-purple-50);
		text-decoration: none;
	}
`;

const ImageNext = styled.img`
	display: block;
	opacity: 0.6;
`;

const Icon = styled.img`
	width: 16px;
	margin: auto;
`;

const Triangle = styled.img`
	position: absolute;
	bottom: -15px;
	left: 50%;
	transform: translateX(-50%) rotate(180deg);
	width: 20px;
	z-index: 9999;
`;

const SelectedItem = styled.div`
	display: flex;
	align-items: center;
	position: relative;
	padding-left: 20px;
	padding-right: 34px;
	height: 46px;
	background-color: transparent;
	border: 2px solid var(--color-purple-alt);
	color: var(--color-purple-alt);
	font-family: var(--font-sans);
	box-sizing: content-box;
	border-radius: 14px;
	font-size: 14px;
	line-height: 42px;
	text-align: center;
	font-weight: var(--font-medium);
	cursor: pointer;
	transition: 0.3s;

	@media (max-width: 1060px) {
		height: 42px;
		padding-left: 18px;
		padding-right: 32px;
	}
`;

const DropdownWrapper = styled.div`
	position: absolute;
	left: 0;
	bottom: 70px;
	opacity: 0;
	transition: 0.3s;
`;

const Wrapper = styled.div`
	position: relative;

	&:hover {
		${SelectedItem} {
			background-color: var(--color-purple);
			border: 2px solid var(--color-purple-alt);
			color: var(--color-white);
			svg {
				transform: translateY(-50%) rotate(0);

				path {
					stroke: var(--color-white);
				}
			}
		}

		${DropdownWrapper} {
			opacity: 1;
		}
	}
`;

const Shades = styled.div`
	&:after {
		content: '';
		position: absolute;
		width: 100%;
		height: 100%;
		top: 20px;
		left: 50%;
		transform: translateX(-50%);
		background-color: var(--color-blue);
		opacity: 0.15;
		filter: blur(32px);
		border-radius: 20px;
		z-index: -1;
	}

	&:before {
		content: '';
		position: absolute;
		width: 100%;
		height: calc(100% - 20px);
		top: 20px;
		left: 50%;
		transform: translateX(-50%) scale(0.8);
		background-color: var(--color-blue);
		opacity: 0.15;
		filter: blur(36px);
		border-radius: 20px;
		z-index: -1;
	}
`;

const DropdownList = styled.div`
	background-color: var(--color-white);
	padding: 15px 10px;
	min-width: 140px;
	z-index: 2000;
	margin-top: 23px;
	--smooth-corners: 11.34, 9.6;
	mask-image: paint(smooth-corners);
`;

const DesktopSocials = styled.div`
	display: flex;
	flex-wrap: wrap;
`;

const DesktopBlock = styled.a`
	width: 50px;
	height: 50px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	background-color: var(--color-white-30);
	border-radius: 12px;
	margin: 5px;
	transition: background-color 0.3s;
	cursor: pointer;

	&:hover {
		background-color: var(--color-purple);

		.svg {
			cursor: pointer;
			filter: brightness(0) invert(1);
		}
	}
	.svg {
		width: 16px;
		height: 16px;
		margin: auto;
	}
`;

export default Footer;
